import { Geotab } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CastedAddInData, useRawAddinData } from "./useAddinData";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { compressData } from "@/utilities/compression";

interface UpdateAddInData<T> {
  geotabClient: Geotab;
  addInId: string;
  details: object;
  keys: (keyof T)[];
  previous: CastedAddInData<T>[];
}

const matchesKeys = <T>(item: CastedAddInData<T>, details: T, keys: (keyof T)[]): boolean =>
  keys.every(k => item.details[k] === details[k]);

// Add or update AddIn by composite key (data[i][keys[0]], data[i][keys[1]], ...)
export async function updateAddIn<T>({
  geotabClient,
  addInId,
  keys,
  details,
  previous,
}: UpdateAddInData<T>): Promise<boolean> {
  let method = "Add";
  const newAddin: {
    id?: string;
    addInId: string;
    details: object;
  } = {
    addInId,
    details: compressData(details),
  };

  try {
    if (previous) {
      const alreadyWritten = previous.filter(item => matchesKeys(item, details as T, keys));
      if (alreadyWritten?.length > 0) {
        method = "Set";
        newAddin["id"] = alreadyWritten[0].id;
      }
    }
    await geotabClient.call(method, { typeName: "AddInData", entity: newAddin });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function useUpdateAddIn<T extends Record<string, any>>(
  addInId: string,
  keys: keyof T | (keyof T)[],
) {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { data: previous = [] } = useRawAddinData<T>(addInId);

  const keyList: (keyof T)[] = Array.isArray(keys) ? keys : [keys];

  const queryKey = ["addinData", addInId, activeSessionId];

  return useMutation({
    mutationFn: async (details: T) => {
      return updateAddIn<T>({
        geotabClient,
        addInId,
        keys: keyList,
        details,
        previous,
      });
    },
    onMutate: async (newDetails: T) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<CastedAddInData<T>[]>(queryKey) ?? [];

      const updatedData = previousData.map(item =>
        matchesKeys(item, newDetails, keyList) ? { ...item, details: newDetails } : item,
      );

      const exists = previousData.some(item => matchesKeys(item, newDetails, keyList));
      const finalData = exists
        ? updatedData
        : [...previousData, { id: `temp-${Date.now()}`, addInId, details: newDetails }];

      queryClient.setQueryData<CastedAddInData<T>[]>(queryKey, finalData);

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<CastedAddInData<T>[]>(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
